import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import { Delete, Download, UploadFile } from '@mui/icons-material'
import {
    Alert,
    AlertTitle,
    Box,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material'
import {
    deleteDocument,
    downloadDocument,
    updateDocumentType,
} from 'candidate/graphql/profile.gql'
import config from 'config'
import React, { Fragment, useCallback, useState } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import { DropdownOption } from 'shared/models/dropdownOption'
import {
    SectionHeaderContainer,
    SectionTitleContainer,
    Separator,
} from 'shared/style/styled'
import { getPxAsRem } from 'shared/utils'
import { parsedDateAndMonth } from 'shared/utils/dateFormatter'
import ConfirmDialog from './ConfirmDialog'
import { GetCandidateProfileForCandidateQuery } from '__generated__/graphql'

const baseStyle = {
    padding: getPxAsRem(24, 16),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    marginTop: getPxAsRem(8),
}

interface Props {
    loading?: boolean
    id: string
    data: GetCandidateProfileForCandidateQuery
    refetch: () => Promise<void>
}

//temporary master data
const documentType = [
    { id: 'offerLetter', value: 'Offer Letter' },
    { id: 'resume', value: 'Resume' },
    { id: 'dealSheetTrackRecord', value: 'Deal Sheet/Track Record' },
    { id: 'review', value: 'Review' },
    { id: 'bio', value: 'Bio' },
    { id: 'other', value: 'Other' },
]

const DocumentUpload: React.FC<Props> = ({ data, refetch }) => {
    const toast = useToast()
    const documents = data.candidate?.documents ?? []
    const theme = useTheme()
    const isMobile = useIsMobile()
    const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)
    const [documentId, setDocumentId] = useState<string>('')
    const [message, setMessage] = useState<string>('')
    const [title, setTitle] = useState<string>('')
    const [action, setAction] = useState<string>('')
    const [deleteMutation] = useMutation(deleteDocument)
    const [updateTypeMutation] = useMutation(updateDocumentType)
    const [download] = useLazyQuery(downloadDocument, {
        fetchPolicy: 'network-only',
    })

    async function uploadDocument(file: File) {
        const formData = new FormData()
        formData.append('document', file)
        try {
            const response = await fetch(
                `${config.api.uri}/candidateDocumentUpload`,
                {
                    method: 'POST',
                    body: formData,
                    headers: {
                        contentType: 'multipart/form-data',
                        Authorization:
                            'Bearer ' + localStorage.getItem('token'),
                        boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                    },
                }
            )
            const data = await response.json()
            if (response.ok) {
                console.log('Document uploaded successfully: ', data)
                setIsFileUploaded(true)
                refetch()
                toast({
                    title: 'Document Uploaded',
                    message: `${file.name} has been uploaded`,
                    severity: 'success',
                })
            } else {
                console.error('Failed to upload document: ', data)
                setIsFileUploaded(false)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('Failed to upload document', error)
            setIsFileUploaded(false)
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        }
    }

    const onDrop = useCallback((acceptedFiles) => {
        acceptedFiles.forEach((file: File) => {
            uploadDocument(file)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const accept = {
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        'application/pdf': ['.pdf'],
    }
    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize: 3000000,
        onDrop,
        multiple: false,
    })

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        const FileRejectionMessage = ({
            code,
            message,
        }: {
            code: string
            message: string
        }) => {
            switch (code) {
                case ErrorCode.FileInvalidType:
                    const acceptedTypes = Object.values(accept)
                        .flat()
                        .join(', ')
                    return <>File must be type: {acceptedTypes}</>
                case ErrorCode.FileTooLarge:
                case ErrorCode.FileTooSmall:
                case ErrorCode.TooManyFiles:
                    return <>{message}</>
            }
        }

        return (
            <Stack sx={{}}>
                <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    <ul>
                        {errors.map((e) => (
                            <>
                                <li key={e.code}>
                                    {file.name} -{' '}
                                    <FileRejectionMessage
                                        code={e.code}
                                        message={e.message}
                                    />
                                </li>
                            </>
                        ))}
                    </ul>
                </Alert>
            </Stack>
        )
    })

    const formatDocumentKey = (docKey: string) => {
        // remove the folder path and candidateId from the document key (if present, seed data doesn't have it)
        if (docKey.includes('/')) {
            const splitDocKey = docKey.split('/')
            return splitDocKey[splitDocKey.length - 1]
        } else {
            return docKey
        }
    }

    const handleDocumentUpdate = async (
        documentTypeSelection: DropdownOption | undefined,
        id: string
    ) => {
        if (documentTypeSelection?.value === 'Resume') {
            // open confirmation modal
            setOpen(true)
            setDocumentId(id)
            const documentChangeMessage = `
        If you mark this as a resume, it will replace the previous one.
        Make sure you have a copy of the old resume if you need to keep it.`
            setMessage(documentChangeMessage)
            setTitle('Warning')
            setAction('documentChange')
        } else {
            try {
                // autosave
                await updateTypeMutation({
                    variables: {
                        documentId: id,
                        documentType: documentTypeSelection?.value || '',
                    },
                })
                refetch()
            } catch (error) {
                console.log(error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again later.',
                })
            }
        }
    }

    const handleCloseModal = () => {
        setOpen(false)
        setDocumentId('')
        setAction('')
        setTitle('')
        setMessage('')
    }

    const handleDeleteIconClick = (id: string) => {
        setDocumentId(id)
        setOpen(true)
        setMessage('This file will be permanently removed.')
        setTitle('Are you sure?')
        setAction('delete')
    }

    const handleDownloadClick = async (id: string) => {
        const { data } = await download({ variables: { documentId: id } })
        if (data?.download?.url) {
            const hiddenDownloadLink = document.createElement('a')
            hiddenDownloadLink.href = data.download.url
            hiddenDownloadLink.target = '_blank'
            hiddenDownloadLink.click()
            hiddenDownloadLink.remove()
        } else {
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }

    const handleConfirmModal = async () => {
        try {
            if (action === 'delete') {
                await deleteMutation({ variables: { documentId } })
            } else if (action === 'documentChange') {
                await updateTypeMutation({
                    variables: {
                        documentId,
                        documentType: 'Resume',
                    },
                })
            }
            refetch()
            setOpen(false)
            setDocumentId('')
            setAction('')
            setTitle('')
            setMessage('')
        } catch (error) {
            console.log(error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }
    return (
        <>
            <ConfirmDialog
                open={open}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirmModal}
                message={message}
                title={title}
            />
            <SectionHeaderContainer>
                <SectionTitleContainer>
                    <Typography variant="body2">Documents</Typography>
                </SectionTitleContainer>
            </SectionHeaderContainer>
            <Box
                sx={{
                    ...baseStyle,
                }}
            >
                <div
                    {...getRootProps()}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <input {...getInputProps()} />
                    <IconButton size="small">
                        <UploadFile color="primary" />
                    </IconButton>
                    <Typography sx={{ py: getPxAsRem(4) }}>
                        <Link underline="hover" color="primary">
                            Click to upload
                        </Link>
                        &nbsp; or drag and drop
                    </Typography>
                    <Typography>.doc, .docx, .pdf</Typography>
                </div>
            </Box>
            {fileRejectionItems.length > 0 ? (
                <aside>
                    <ul>{fileRejectionItems}</ul>
                </aside>
            ) : (
                <div>{isFileUploaded ? 'File uploaded successfully' : ''}</div>
            )}
            {documents.length > 0 ? (
                <Grid
                    container
                    sx={{
                        marginTop: getPxAsRem(16),
                        padding: getPxAsRem(16),
                    }}
                >
                    {documents.map((doc, index) => (
                        <Fragment key={`doc_${doc.id}`}>
                            <Grid item xs={12}>
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    justifyContent={
                                        isMobile ? 'center' : 'space-between'
                                    }
                                    alignItems={
                                        isMobile ? 'flex-start' : 'center'
                                    }
                                    rowGap={1}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <UploadFile
                                            color="primary"
                                            fontSize="small"
                                        />
                                        <Box sx={{ px: getPxAsRem(16) }}>
                                            <Typography>
                                                {formatDocumentKey(
                                                    doc.documentKey
                                                )}{' '}
                                            </Typography>
                                            <Box display={'flex'}>
                                                <Typography variant="body2">
                                                    {parsedDateAndMonth(
                                                        new Date(
                                                            doc.updatedAt ??
                                                                doc.createdAt
                                                        ).toLocaleString()
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Stack
                                        direction={
                                            isMobile ? 'row-reverse' : 'row'
                                        }
                                    >
                                        <Box
                                            sx={{
                                                padding: getPxAsRem(0, 16),
                                                alignItems: 'center',
                                                display: 'flex',
                                                flexShrink: 0,
                                                minWidth: '15.5rem',
                                            }}
                                        >
                                            <FormControl fullWidth>
                                                <InputLabel
                                                    id={`document_type${index}_label`}
                                                >
                                                    Choose Document Type
                                                </InputLabel>
                                                <Select
                                                    fullWidth
                                                    id={`document_type${index}`}
                                                    labelId={`document_type${index}_label`}
                                                    label={
                                                        'Choose Document Type'
                                                    }
                                                    onChange={(e) => {
                                                        const documentTypeSelection =
                                                            documentType.find(
                                                                ({ value }) =>
                                                                    value ===
                                                                    e.target
                                                                        .value
                                                            )
                                                        handleDocumentUpdate(
                                                            documentTypeSelection,
                                                            doc.id
                                                        )
                                                    }}
                                                    value={doc.documentType}
                                                    disabled={false}
                                                    inputProps={{
                                                        sx: {
                                                            padding: `${getPxAsRem(8, 35, 8, 12)} !important`,
                                                            border: !doc.documentType
                                                                ? `1px solid ${theme.palette.error.main}`
                                                                : undefined,
                                                        },
                                                    }}
                                                >
                                                    {documentType.map(
                                                        (item) => (
                                                            <MenuItem
                                                                key={item.id}
                                                                value={
                                                                    item.value
                                                                }
                                                            >
                                                                {item.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-around',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDownloadClick(doc.id)
                                                }
                                            >
                                                <Download fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDeleteIconClick(
                                                        doc.id
                                                    )
                                                }
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Separator />
                            </Grid>
                        </Fragment>
                    ))}
                </Grid>
            ) : (
                ''
            )}
        </>
    )
}

export default DocumentUpload
